/**
 * Radar/spider chart showing campaign health scored on multiple KPIs using Nivo Radar.
 */

import { ResponsiveRadar } from '@nivo/radar'
import { useNivoTheme, useChartColors } from './nivoTheme'

export interface RadarMetric {
  metric: string
  value: number
  rawValue: string
}

export interface CampaignRadarData {
  campaign: string
  metrics: RadarMetric[]
}

interface CampaignHealthRadarProps {
  data: CampaignRadarData[]
  height?: number
  highlights?: string[]
}

export function CampaignHealthRadar({
  data,
  height = 350,
  highlights: _highlights,
}: CampaignHealthRadarProps) {
  const theme = useNivoTheme()
  const chartColors = useChartColors()

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        No campaign health data available
      </div>
    )
  }

  // Build a lookup of raw values keyed by "campaign|metric"
  const rawValueLookup: Record<string, string> = {}
  data.forEach((campaign) => {
    campaign.metrics.forEach((m) => {
      rawValueLookup[`${campaign.campaign}|${m.metric}`] = m.rawValue
    })
  })

  // Get all unique metric names from the first campaign
  const metricNames = data[0].metrics.map((m) => m.metric)

  // Campaign keys for Nivo
  const campaignKeys = data.map((d) => d.campaign)

  // Transform to Nivo radar format: [{ metric: "CTR", Campaign1: 75, Campaign2: 60 }]
  const radarData = metricNames.map((metricName) => {
    const row: Record<string, string | number> = { metric: metricName }
    data.forEach((campaign) => {
      const found = campaign.metrics.find((m) => m.metric === metricName)
      row[campaign.campaign] = found?.value ?? 0
    })
    return row
  })

  // Assign colors per campaign
  const colorMap: Record<string, string> = {}
  campaignKeys.forEach((key, i) => {
    colorMap[key] = chartColors[i % chartColors.length]
  })

  return (
    <div style={{ height }}>
      <ResponsiveRadar
        data={radarData}
        keys={campaignKeys}
        indexBy="metric"
        theme={theme}
        maxValue={100}
        margin={{ top: 40, right: 60, bottom: 40, left: 60 }}
        gridLevels={5}
        gridShape="circular"
        dotSize={8}
        dotBorderWidth={2}
        dotBorderColor={{ from: 'color' }}
        dotColor={{ theme: 'background' }}
        colors={(d) => colorMap[d.key as string] ?? chartColors[0]}
        blendMode="normal"
        fillOpacity={0.15}
        borderWidth={2}
        motionConfig="gentle"
        sliceTooltip={({ index, data: sliceData }) => (
          <div className="bg-popover rounded-lg shadow-lg px-3 py-2 text-sm border border-border">
            <p className="font-semibold text-popover-foreground mb-1">{index}</p>
            {sliceData.map((item) => {
              const raw = rawValueLookup[`${item.id}|${index}`] ?? ''
              return (
                <div key={item.id} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-muted-foreground">{item.id}:</span>
                  <span className="font-medium">{raw}</span>
                  <span className="text-muted-foreground">({item.value})</span>
                </div>
              )
            })}
          </div>
        )}
        legends={campaignKeys.length > 1 ? [{
          anchor: 'top-right' as const,
          direction: 'column' as const,
          translateX: 60,
          translateY: -20,
          itemsSpacing: 4,
          itemWidth: 80,
          itemHeight: 16,
          symbolSize: 10,
          symbolShape: 'circle',
        }] : []}
      />
    </div>
  )
}
